import type { ReactElement } from 'react';
import { useEffect } from 'react';

import isNil from 'lodash/isNil';
import { Trans, useTranslation } from 'react-i18next';
import {
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router';
import styled from 'styled-components';

import { CheckCircle, XCircle } from '@phosphor-icons/react';

import Rows from '@virtuslab/nfs-shared/src/components/atoms/Rows';
import ActionText from '@virtuslab/nfs-shared/src/components/molecules/ActionText';
import Body from '@virtuslab/nfs-shared/src/components/molecules/Body';
import Button from '@virtuslab/nfs-shared/src/components/molecules/Button';
import CTAButton from '@virtuslab/nfs-shared/src/components/molecules/CTAButton';
import Header from '@virtuslab/nfs-shared/src/components/molecules/Header';
import Icon from '@virtuslab/nfs-shared/src/components/molecules/Icon';
import ConfirmationModal from '@virtuslab/nfs-shared/src/components/organisms/ConfirmationModal';
import {
  externalLinks,
  profileCompletionGuardStorageKey,
  profileCompletionMinSkills,
} from '@virtuslab/nfs-shared/src/config/component';
import { useMyProfileQuery } from '@virtuslab/nfs-shared/src/schema/user';
import { isEmpty } from '@virtuslab/nfs-shared/src/services/checks';
import { splitError } from '@virtuslab/nfs-shared/src/services/errors';
import { isNotNil } from '@virtuslab/nfs-shared/src/services/guards';
import { usePersistentStorage } from '@virtuslab/nfs-shared/src/services/hooks/useLocalStorage';
import type { Nullable } from '@virtuslab/nfs-shared/src/services/object';
import { generatePath } from '@virtuslab/nfs-shared/src/services/routes';

import { MyProfileRoutes } from '../../../config/paths';

type ChecklistItemProps = Readonly<{
  done: boolean;
  label: string;
}>;

const ChecklistRow = styled.div`
  align-items: center;
  display: grid;
  gap: ${({ theme }) => theme.scTheme.space[200]};
  grid-template-columns: auto 1fr;
`;

const ChecklistItem = ({ done, label }: ChecklistItemProps): ReactElement => (
  <ChecklistRow>
    <Body size={300} variant={done ? 'success' : 'alert'}>
      <Icon size="md">
        {done ? <CheckCircle weight="fill" /> : <XCircle weight="fill" />}
      </Icon>
    </Body>
    <Body size={300} variant={done ? 'secondary' : 'primary'} fitToWidth>
      {label}
    </Body>
  </ChecklistRow>
);

const ProfileCompletionGuard = (): Nullable<ReactElement> => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [dismissed, setDismissed] = usePersistentStorage(
    profileCompletionGuardStorageKey,
    false,
    { storage: 'session' },
  );

  const { data, startPolling, stopPolling } = useMyProfileQuery({ skip: dismissed });

  const [user, error] = splitError(data?.profile);
  const hasError = isNotNil(error);

  useEffect(() => {
    if (!hasError) {
      return undefined;
    }
    startPolling(10_000);
    return () => {
      stopPolling();
    };
  }, [hasError, startPolling, stopPolling]);

  if (dismissed || hasError || isNil(user)) {
    return null;
  }

  const hasSkills = user.skillsAndCompetences.length >= profileCompletionMinSkills;
  const hasCertificates = !isEmpty(user.certificates) || user.declaredNoCertificates;
  const hasArea = !isEmpty(user.areaOfExpertise);

  if (hasSkills && hasCertificates && hasArea) {
    return null;
  }

  if (isNotNil(matchPath(MyProfileRoutes.MY_PROFILE, location.pathname))) {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
  };

  const goToProfile = () => {
    navigate(generatePath(MyProfileRoutes.MY_PROFILE));
    dismiss();
  };

  return (
    <ConfirmationModal
      width="profileCompletionModal"
      header={<Header as="h4">{t('Complete your profile')}</Header>}
      body={(
        <Rows gap={400}>
          <Body size={300}>{t('Please complete the following:')}</Body>
          <Rows gap={200}>
            <ChecklistItem
              done={hasSkills}
              label={t('Add at least {{count}} skills', { count: profileCompletionMinSkills })}
            />
            <ChecklistItem
              done={hasCertificates}
              label={t('Declare your certificates or indicate you have none to declare')}
            />
            <ChecklistItem
              done={hasArea}
              label={t('Add your area of expertise')}
            />
          </Rows>
          <Body size={300} display="inline">
            <Trans
              defaults="For more context, please check the <slackLink>Slack announcement</slackLink>."
              components={{
                slackLink: (
                  <ActionText
                    as="a"
                    href={externalLinks.profileCompletion}
                    target="_blank"
                    rel="noopener noreferrer"
                    size={300}
                  />
                ),
              }}
            />
          </Body>
        </Rows>
      )}
      onConfirm={goToProfile}
      onDeny={dismiss}
      confirmButton={(
        <CTAButton
          onClick={goToProfile}
          ref={(node) => node?.focus()}
        >
          {t('Go to My Profile')}
        </CTAButton>
      )}
      closeButton={(
        <Button onClick={dismiss} variant="secondary">
          {t('I understand')}
        </Button>
      )}
    />
  );
};

export default ProfileCompletionGuard;
